import { useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Badge } from '@/shared/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { adminRolePath, adminRolesPath } from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'

interface UserWithRole {
  id: number
  email: string
  firstName: string
  lastName: string
  role: string | null
  roleDisplayName: string | null
}

interface RoleOption {
  value: string
  label: string
  description: string
}

interface Props {
  users: UserWithRole[]
  pagination: PaginationMeta
  roles: RoleOption[]
  [key: string]: unknown
}

const RoleManagementPage = () => {
  const { users, pagination, roles } = usePage<Props>().props

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.adminRoles') }])

  const [search, setSearch] = useState('')
  const [removeTargetId, setRemoveTargetId] = useState<number | null>(null)
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null)

  const removeForm = useForm({})

  const isAdmin = useCan(Resources.RBAC, Actions.DELETE)
  const canAssignRole = useCan(Resources.RBAC, Actions.UPDATE)

  const filteredUsers = users.filter((user) => {
    const query = search.toLowerCase()
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    return fullName.includes(query) || user.email.toLowerCase().includes(query)
  })

  const handleRoleChange = (userId: number, newRole: string) => {
    setUpdatingRoleUserId(userId)
    router.patch(
      adminRolePath(userId),
      { role: newRole },
      {
        preserveScroll: true,
        onFinish: () => setUpdatingRoleUserId(null),
      },
    )
  }

  const handleRemoveConfirm = () => {
    if (removeTargetId === null) return
    removeForm.delete(adminRolePath(removeTargetId), {
      preserveScroll: true,
      onFinish: () => setRemoveTargetId(null),
    })
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('admin.roles.title')}</h1>

      <div className="flex items-center gap-4">
        <Input
          placeholder={t('admin.roles.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('admin.roles.table.noRole')}</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div key={user.id} className="rounded-lg border bg-card p-3 shadow-sm space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                {user.roleDisplayName ? (
                  <Badge variant="secondary" className="shrink-0">
                    {user.roleDisplayName}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {t('admin.roles.table.noRole')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {canAssignRole && (
                  <Select
                    value={user.role ?? undefined}
                    onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                    disabled={updatingRoleUserId === user.id}
                  >
                    <SelectTrigger size="sm" className="w-36">
                      <SelectValue placeholder={t('admin.roles.assignRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {isAdmin && user.role && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setRemoveTargetId(user.id)}
                    disabled={removeForm.processing}
                  >
                    {t('admin.roles.removeRole')}
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.roles.table.name')}</TableHead>
              <TableHead>{t('admin.roles.table.email')}</TableHead>
              <TableHead>{t('admin.roles.table.role')}</TableHead>
              <TableHead>{t('admin.roles.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.roleDisplayName ? (
                    <Badge variant="secondary">{user.roleDisplayName}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      {t('admin.roles.table.noRole')}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {canAssignRole && (
                      <Select
                        value={user.role ?? undefined}
                        onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                        disabled={updatingRoleUserId === user.id}
                      >
                        <SelectTrigger size="sm" className="w-36">
                          <SelectValue placeholder={t('admin.roles.assignRole')} />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {isAdmin && user.role && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setRemoveTargetId(user.id)}
                        disabled={removeForm.processing}
                      >
                        {t('admin.roles.removeRole')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {pagination.page} of {pagination.totalPages} (
            {search ? `${filteredUsers.length} shown of ` : ''}
            {pagination.total} users)
          </span>
          <div className="flex gap-2">
            {pagination.page > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams(window.location.search)
                  params.set('page', String(pagination.page - 1))
                  router.visit(`${adminRolesPath()}?${params}`, { preserveState: true })
                }}
              >
                {t('admin.roles.previous')}
              </Button>
            )}
            {pagination.page < pagination.totalPages && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams(window.location.search)
                  params.set('page', String(pagination.page + 1))
                  router.visit(`${adminRolesPath()}?${params}`, { preserveState: true })
                }}
              >
                {t('admin.roles.next')}
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={removeTargetId !== null}
        onOpenChange={(open) => !open && setRemoveTargetId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.roles.removeConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('admin.roles.removeConfirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTargetId(null)}>
              {t('admin.roles.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveConfirm}
              disabled={removeForm.processing}
            >
              {t('admin.roles.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RoleManagementPage
